import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    createStyles,
    makeStyles
} from '@material-ui/core';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { MarketplacePetTag } from '@spike/marketplace-model';

interface DetailTableProps {
    personalities: Array<MarketplacePetTag>;
    loading: boolean;
    openModalDelete?: (petTag: MarketplacePetTag) => void;
    editPetTagHandle?: (petTag: MarketplacePetTag) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            'overflowX': 'scroll',

            '&::-webkit-scrollbar': {
                display: 'none'
            }
        },
        tableHead: {
            'borderBottom': 'solid 2px #000',

            '& .MuiTableCell-head': {
                'fontSize': 16,
                'lineHeight': 1.5,
                'fontWeight': 600,

                '&:first-child': {
                    'left': 0,
                    'position': 'sticky',

                    [theme.breakpoints.up('md')]: {
                        paddingLeft: 35
                    },

                    '&:before': {
                        'content': `''`,
                        'top': 0,
                        'left': 0,
                        'right': 0,
                        'bottom': 0,
                        'zIndex': -1,
                        'display': 'block',
                        'position': 'absolute',
                        'backgroundColor': '#fff',
                        'boxShadow': '0px 0px 0px 0px transparent',
                        'transition': 'box-shadow 0.15s ease-out',

                        '.scrolled &': {
                            boxShadow: '8px 0px 10px 0px rgba(0, 0, 0, 0.08)'
                        }
                    }
                },

                '&:last-child': {
                    [theme.breakpoints.only('xs')]: {
                        minWidth: 80
                    }
                }
            }
        },
        tableBody: {
            'whiteSpace': 'nowrap',

            '& .MuiTableCell-body': {
                'height': '43px !important',
                'fontSize': 16,
                'lineHeight': 1,
                'borderBottomColor': '#D4D4D4',

                [theme.breakpoints.up('md')]: {
                    height: 54
                },

                '&:first-child': {
                    'left': 0,
                    'fontWeight': 500,
                    'paddingLeft': 35,
                    'position': 'sticky',

                    '&:before': {
                        'content': `''`,
                        'top': 1,
                        'left': 0,
                        'right': 0,
                        'bottom': 1,
                        'zIndex': -1,
                        'display': 'block',
                        'position': 'absolute',
                        'backgroundColor': '#fff',
                        'boxShadow': '0px 0px 0px 0px transparent',
                        'transition': 'box-shadow 0.15s ease-out',

                        '.scrolled &': {
                            boxShadow: '8px 0px 10px 0px rgba(0, 0, 0, 0.08)'
                        }
                    }
                }
            },

            '& .MuiTableRow-root': {
                '&:last-child': {
                    '& .MuiTableCell-body': {
                        borderBottom: 0
                    }
                }
            },

            '& b, & a': {
                fontWeight: 500
            },
            '& a': {
                'fontSize': 16,
                'color': '#222',
                'lineHeight': 1.5,
                'position': 'relative',
                'display': 'inline-block',
                'textDecoration': 'none',

                [theme.breakpoints.up('md')]: {
                    borderBottom: 'solid 1.5px #222'
                },

                '& span': {
                    [theme.breakpoints.down('sm')]: {
                        display: 'none'
                    }
                },
                '& svg': {
                    [theme.breakpoints.up('md')]: {
                        display: 'none'
                    }
                }
            }
        },
        mobile: {
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        defaultTag: {
            background: '#F4F3F0',
            borderRadius: '14px',
            padding: '4px 6px',
            fontWeight: 500,
            fontSize: 13,
            marginLeft: '10px'
        },
        trashIconBox: {
            display: 'flex',
            width: '10%'
        },
        trashIcon: {
            fontSize: `$16px`,
            cursor: 'pointer',
            color: 'black',
            opacity: 1,
            marginTop: 'auto',
            marginBottom: 'auto',
            paddingLeft: '18px',
            paddingTop: '20px'
        },

        trashIconDisabled: {
            cursor: 'default',
            opacity: 0.7
        }
    })
);

const PersonalityRow: React.FunctionComponent<{
    personality: MarketplacePetTag;
    loading: boolean;
    openModalDelete?: (petTag: MarketplacePetTag) => void;
    editPetTagHandle?: (petTag: MarketplacePetTag) => void;
}> = ({ personality, loading, editPetTagHandle, openModalDelete }) => {
    const classes = useStyles();

    const clickEditPetTagHandle = (petTag: MarketplacePetTag) => {
        editPetTagHandle && editPetTagHandle(petTag);
    };

    const clickDeletePetTagHandle = (petTag: MarketplacePetTag) => {
        openModalDelete && openModalDelete(petTag);
    };

    return (
        <TableRow>
            <TableCell>
                <b>{personality.name}</b>
            </TableCell>
            <TableCell>{personality.count}</TableCell>
            <TableCell>
                {personality.source === 'default' ? (
                    <span className={classes.defaultTag}>Default</span>
                ) : (
                    <Box className={classes.trashIconBox}>
                        <FontAwesomeIcon
                            id={'business_settings_taxes_icon_delete_'}
                            icon={faPencil}
                            className={
                                !loading ? classes.trashIcon : clsx(classes.trashIcon, classes.trashIconDisabled)
                            }
                            onClick={() => clickEditPetTagHandle(personality)}
                        />

                        <FontAwesomeIcon
                            id={'business_settings_taxes_icon_delete_'}
                            icon={faTrashAlt}
                            className={
                                !loading ? classes.trashIcon : clsx(classes.trashIcon, classes.trashIconDisabled)
                            }
                            onClick={() => clickDeletePetTagHandle(personality)}
                        />
                    </Box>
                )}
            </TableCell>
        </TableRow>
    );
};

export const DetailTable: React.FunctionComponent<DetailTableProps> = ({
    personalities,
    loading,
    openModalDelete,
    editPetTagHandle
}) => {
    const classes = useStyles();
    const ref = useRef<HTMLDivElement>(null);

    const [scrollPos, setScrollPos] = useState(0);

    // The scroll listener
    const handleScroll = useCallback((event: Event) => {
        const div = event.target as HTMLDivElement;

        setScrollPos(div.scrollLeft);
    }, []);

    // Attach the scroll listener to the div
    useEffect(() => {
        const div = ref.current;

        div?.addEventListener('scroll', handleScroll);
    }, [handleScroll]);

    return (
        <div
            className={classes.root}
            ref={ref}
        >
            <Table
                className={clsx({
                    scrolled: scrollPos > 0
                })}
            >
                <TableHead className={classes.tableHead}>
                    <TableRow>
                        <TableCell>Personality</TableCell>
                        <TableCell>Assigned Pets</TableCell>
                        <TableCell>
                            <span className={classes.mobile}></span>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                    {personalities.map((personality, i) => (
                        <PersonalityRow
                            key={i}
                            personality={personality}
                            loading={loading}
                            editPetTagHandle={editPetTagHandle}
                            openModalDelete={openModalDelete}
                        />
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};
